/**
 * 페이지 타이틀 관리 Composable
 * - menuSections에서 현재 경로에 해당하는 메뉴의 타이틀을 자동으로 찾아 표시합니다.
 * - definePageMeta의 title이 명시적으로 설정된 경우 우선 사용합니다.
 * - ASIS(iframe) 모드일 때는 layout에서 provide된 타이틀을 우선 사용합니다.
 * - TheHeader 컴포넌트에서 사용됩니다.
 */
import { computed, inject } from 'vue';
import type { ComputedRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useMenuStore } from '~/stores/useMenuStore';

export const usePageTitle = () => {
  const route = useRoute();
  const menuStore = useMenuStore();

  // ★ storeToRefs로 반응성 있는 참조 생성 (computed 내에서 제대로 추적됨)
  const { sections: menuSections } = storeToRefs(menuStore);

  // layout에서 provide된 ASIS(iframe) 모드 정보와 타이틀 가져오기
  const shouldRenderAsis = inject<ComputedRef<boolean>>(
    'shouldRenderAsis',
    computed(() => false),
  );
  const currentIframeTitle = inject<ComputedRef<string | null>>(
    'currentIframeTitle',
    computed(() => null),
  );

  /** `/screens/...` URL은 메뉴 path(`/task/...` 등)와 맞춰 조회 */
  const normalizePathForMenuLookup = (path: string): string => {
    if (path.startsWith('/screens/')) {
      return path.slice('/screens'.length) || '/';
    }
    return path;
  };

  /**
   * menuSections에서 현재 경로에 해당하는 메뉴 타이틀 찾기
   * @param sections 메뉴 섹션 배열 (반응성 추적을 위해 파라미터로 받음)
   * @param currentPath 현재 경로 (이미 normalizePathForMenuLookup 적용 권장)
   */
  const findTitleFromMenu = (sections: any[], currentPath: string): string | null => {
    if (!sections || sections.length === 0) {
      return null;
    }

    // 모든 메뉴 아이템 검색
    for (const section of sections) {
      // submenus 검색
      if (section.submenus && Array.isArray(section.submenus)) {
        for (const submenu of section.submenus) {
          if (submenu.items && Array.isArray(submenu.items)) {
            for (const item of submenu.items) {
              // 메뉴 아이템의 path가 현재 경로와 일치하는 경우
              if (item.path && currentPath === item.path) {
                return item.name || item.pgmNm;
              }

              // 탭이 있는 경우 탭 경로도 확인
              if (item.tabs && Array.isArray(item.tabs)) {
                for (const tab of item.tabs) {
                  if (tab.path && currentPath === tab.path) {
                    return item.name || item.pgmNm;
                  }
                }
              }
            }
          }
        }
      }

      // items 검색 (submenus가 아닌 direct items)
      if (section.items && Array.isArray(section.items)) {
        for (const item of section.items) {
          if (item.path && currentPath === item.path) {
            return item.name || item.pgmNm;
          }

          if (item.tabs && Array.isArray(item.tabs)) {
            for (const tab of item.tabs) {
              if (tab.path && currentPath === tab.path) {
                return item.name || item.pgmNm;
              }
            }
          }
        }
      }
    }

    return null;
  };

  /**
   * 현재 라우트의 페이지 타이틀
   * 우선순위:
   * 1. route.meta.title (명시적으로 설정된 경우)
   * 2. iframe 모드일 때 layout에서 제공하는 타이틀
   * 3. menuSections에서 찾은 타이틀
   * 4. 기본값 'Dashboard'
   */
  const pageTitle = computed(() => {
    // ★ route.fullPath를 명시적으로 접근하여 라우트 변경 시 computed 재평가 보장
    // (Nuxt 3에서 route.path만 접근하면 의존성 추적이 안 되는 경우가 있음)
    const _fullPath = route.fullPath;
    const currentPath = route.path;
    const menuLookupPath = normalizePathForMenuLookup(currentPath);

    // 1. 명시적으로 설정된 title이 있으면 사용
    if (route.meta.title) {
      return route.meta.title as string;
    }

    // 2. ASIS(iframe) 모드일 때 layout에서 제공하는 타이틀 사용
    if (shouldRenderAsis.value && currentIframeTitle.value) {
      return currentIframeTitle.value;
    }

    // 3. menuSections에서 현재 경로에 해당하는 타이틀 찾기
    // ★ menuSections.value를 직접 접근하여 반응성 의존성 추적
    const sections = menuSections.value;
    const menuTitle = findTitleFromMenu(sections, menuLookupPath);

    if (menuTitle) {
      return menuTitle;
    }

    // 4. 기본값
    return 'Dashboard';
  });

  return {
    pageTitle,
  };
};
